import React, { Component } from 'react';
import { 
  ArrowLeftIcon, 
  EnvelopeIcon, 
  PaperClipIcon, 
  ArrowPathIcon,
  ChevronDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import LoadingSpinner from '../common/LoadingSpinner';

class Emails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      emails: [],
      currentPage: 0,
      totalRecords: 0,
      limit: 10,
      hasMore: false,
      loadingMore: false,
      expandedEmails: new Set(), // Track which emails are expanded
    };
    this.scrollContainerRef = React.createRef();
  }

  componentDidMount() {
    this.loadEmails();
  }

  componentWillUnmount() {
    // Clean up scroll listener
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  loadEmails = async (page = 0) => {
    const { recordId, relationId, onLogout } = this.props;

    // Set loading state based on whether this is initial load or load more
    if (page === 0) {
      this.setState({ loading: true });
    } else {
      this.setState({ loadingMore: true });
    }
    
    try {
      const result = await apiService.getSpecialRelatedRecords(
        'GetRelatedEmails',
        recordId,
        relationId,
        'Emails',
        page,
        this.state.limit
      );
      
      
      if (result.success) {
        const responseData = result.data;
        
        const emails = responseData.listViewEntries || responseData.data || responseData.records || [];
        const totalCount = responseData.total_count || responseData.totalRecords || emails.length;
        
        
        const newEmails = emails;
        
        // Calculate if there are more emails to load
        const currentEmailCount = page === 0 ? newEmails.length : this.state.emails.length + newEmails.length;
        const hasMore = currentEmailCount < totalCount;
        
        this.setState({
          emails: page === 0 ? newEmails : [...this.state.emails, ...newEmails],
          currentPage: page,
          totalRecords: totalCount,
          hasMore: hasMore,
          loading: false,
          loadingMore: false,
        });
        
        // Add scroll listener after first load
        if (page === 0 && this.scrollContainerRef.current) {
          this.scrollContainerRef.current.addEventListener('scroll', this.handleScroll);
        }
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ 
          loading: false, 
          loadingMore: false,
          emails: page === 0 ? [] : this.state.emails 
        });
      }
    } catch (error) {
      this.setState({ 
        loading: false, 
        loadingMore: false,
        emails: page === 0 ? [] : this.state.emails 
      });
    }
  };

  handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const { hasMore, loadingMore } = this.state;
    
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loadingMore) {
      this.loadMoreEmails();
    }
  };

  loadMoreEmails = () => {
    if (this.state.hasMore && !this.state.loadingMore) {
      const nextPage = this.state.currentPage + 1;
      this.loadEmails(nextPage);
    }
  };

  refreshEmails = () => {
    this.setState({
      emails: [],
      currentPage: 0,
      hasMore: false,
      expandedEmails: new Set(),
    }, () => {
      this.loadEmails(0);
    });
  };

  toggleEmailExpansion = (emailId) => {
    this.setState(prevState => {
      const newExpanded = new Set(prevState.expandedEmails);
      if (newExpanded.has(emailId)) {
        newExpanded.delete(emailId);
      } else {
        newExpanded.add(emailId);
      }
      return { expandedEmails: newExpanded };
    });
  };

  handleEmailClick = (email) => {
    this.toggleEmailExpansion(email.id);
  };

  // Decode HTML entities
  decodeHtml = (html) => {
    if (!html) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // Extract plain text from HTML and get first 100 characters
  getEmailPreview = (description, body) => {
    let content = body || description || '';
    content = this.decodeHtml(content);
    
    // Strip HTML tags
    const tmp = document.createElement('div');
    tmp.innerHTML = content;
    const plainText = tmp.textContent || tmp.innerText || '';
    
    // Return first 100 characters
    return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
  };

  // Get full email content
  getFullEmailContent = (description, body) => {
    let content = body || description || '';
    content = this.decodeHtml(content);
    return content;
  };

  // Extract sender name from email address
  getSenderName = (fromEmail) => {
    if (!fromEmail) return 'Unknown Sender';
    
    // If it's just an email, extract the part before @
    if (fromEmail.includes('@') && !fromEmail.includes('<')) {
      const namePart = fromEmail.split('@')[0];
      // Convert to title case and replace dots/underscores with spaces
      return namePart
        .replace(/[._]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // If it's in format "Name <email@domain.com>", extract the name
    if (fromEmail.includes('<')) {
      const name = fromEmail.split('<')[0].trim();
      return name || 'Unknown Sender';
    }
    
    return fromEmail;
  };

  // Format email addresses for display
  formatEmailAddresses = (emails) => {
    if (!emails) return '';
    if (Array.isArray(emails)) {
      return emails.join(', ');
    }
    return emails;
  };

  render() {
    const { 
      loading, 
      emails, 
      totalRecords, 
      hasMore,
      loadingMore,
      expandedEmails 
    } = this.state;
    
    const { onCancel, onBack } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Loading emails..." variant="fullscreen" />
      );
    }

    return (
      <div className="bg-white min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            {onBack ? (
              <button
                onClick={onBack}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={onCancel}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>
            )}
            <button
              onClick={this.refreshEmails}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center">
              <EnvelopeIcon className="w-5 h-5 mr-2 text-blue-600" />
              Emails
            </h1>
            <span className="text-sm text-gray-500">
              {totalRecords} email{totalRecords !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Emails List */}
        <div 
          ref={this.scrollContainerRef}
          className="overflow-y-auto"
          style={{ height: 'calc(100vh - 100px)' }}
          onScroll={this.handleScroll}
        >
          {emails.length > 0 ? (
            <div>
              {emails.map((email, index) => {
                const isExpanded = expandedEmails.has(email.id);
                const hasAttachments = email.related_attachments && email.related_attachments.length > 0;
                const senderName = this.getSenderName(email.from_email);
                const emailPreview = this.getEmailPreview(email.description, email.body);
                
                return (
                  <div key={index} className="border-b border-gray-100">
                    {/* Email Row - Outlook Style */}
                    <div
                      className={`px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                        isExpanded ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => this.handleEmailClick(email)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* Sender Name */}
                          <div className="flex items-center mb-1">
                            <h3 className="font-semibold text-gray-900 text-sm truncate mr-2">
                              {senderName}
                            </h3>
                            {hasAttachments && (
                              <PaperClipIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                          </div>
                          
                          {/* Subject */}
                          <div className="mb-1">
                            <p className="font-medium text-gray-800 text-sm truncate">
                              {email.subject || 'No Subject'}
                            </p>
                          </div>
                          
                          {/* Email Preview */}
                          <div className="mb-1">
                            <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">
                              {emailPreview}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-2 flex-shrink-0 ml-4">
                          {/* Date */}
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {email.date_start}
                          </span>
                          
                          {/* Expand Icon */}
                          <ChevronDownIcon 
                            className={`w-4 h-4 text-gray-400 transition-transform ${
                              isExpanded ? 'transform rotate-180' : ''
                            }`} 
                          />
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-200">
                        <div className="px-4 py-4">
                          {/* Email Details */}
                          <div className="space-y-2 mb-4 text-sm">
                            {/* From */}
                            <div>
                              <span className="font-medium text-gray-700">From: </span>
                              <span className="text-gray-600">{email.from_email}</span>
                            </div>
                            
                            {/* To Recipients */}
                            {email.to_email && this.formatEmailAddresses(email.to_email) && (
                              <div>
                                <span className="font-medium text-gray-700">To: </span>
                                <span className="text-gray-600">
                                  {this.formatEmailAddresses(email.to_email)}
                                </span>
                              </div>
                            )}
                            
                            {/* CC Recipients */}
                            {email.cc_email && this.formatEmailAddresses(email.cc_email) && (
                              <div>
                                <span className="font-medium text-gray-700">CC: </span>
                                <span className="text-gray-600">
                                  {this.formatEmailAddresses(email.cc_email)}
                                </span>
                              </div>
                            )}
                            
                            {/* BCC Recipients */}
                            {email.bcc_email && this.formatEmailAddresses(email.bcc_email) && (
                              <div>
                                <span className="font-medium text-gray-700">BCC: </span>
                                <span className="text-gray-600">
                                  {this.formatEmailAddresses(email.bcc_email)}
                                </span>
                              </div>
                            )}
                            
                            {/* Date */}
                            <div>
                              <span className="font-medium text-gray-700">Date: </span>
                              <span className="text-gray-600">{email.date_start}</span>
                            </div>
                          </div>
                          
                          {/* Attachments */}
                          {hasAttachments && (
                            <div className="mb-4">
                              <div className="flex items-center mb-2">
                                <PaperClipIcon className="w-4 h-4 text-gray-500 mr-2" />
                                <span className="text-sm font-medium text-gray-700">
                                  {email.related_attachments.length} Attachment{email.related_attachments.length > 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {email.related_attachments.map((attachment, attIndex) => (
                                  <div key={attIndex} className="flex items-center justify-between bg-white p-2 rounded border">
                                    <span className="text-sm text-gray-900 truncate">
                                      {attachment.name}
                                    </span>
                                    {attachment.path && (
                                      <a
                                        href={attachment.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 p-1 text-blue-600 hover:text-blue-800 rounded transition-colors"
                                        title="View attachment"
                                      >
                                        <EyeIcon className="w-4 h-4" />
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Email Content */}
                          <div className="bg-white p-4 rounded border">
                            <div 
                              className="text-sm text-gray-700 leading-relaxed"
                              dangerouslySetInnerHTML={{ 
                                __html: this.getFullEmailContent(email.description, email.body) 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Load More Indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-500">Loading more emails...</span>
                  </div>
                </div>
              )}
              
              {/* End of Emails Indicator */}
              {!hasMore && emails.length > 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">No more emails</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <EnvelopeIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Emails Found</h3>
                <p className="text-sm text-gray-500 mb-4">
                  No emails associated with this record.
                </p>
                <button
                  onClick={this.refreshEmails}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Emails;